import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import { Switch } from "@mui/material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import ArgonInput from "components/ArgonInput";
import api from "utils/api";
import { toast } from "react-toastify";
import EditPlanDialog from "../EditPlanDialog";
import EditIcon from "@mui/icons-material/Edit";

function InitializationPlan() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Fetch all plans
  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await api.get("/planwithstudentreview");
      if (response.data.success) {
        setPlans(response.data.data?.plans || []);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
      toast.error(error.response?.data?.message || "Failed to fetch plans");
      setPlans([]);
    } finally {
      // Add a small delay to show loading state
      setTimeout(() => {
        setLoading(false);
      }, 800);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  // Handle edit click
  const handleEditClick = (plan) => {
    setSelectedPlan(plan);
    setEditDialogOpen(true);
  };

  // Handle edit dialog close
  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedPlan(null);
  };

  // Handle plan update
  const handlePlanUpdate = async (updatedPlanData) => {
    setLoading(true);
    try {
      const response = await api.put(
        `/planwithstudentreview/plan/${selectedPlan._id}`,
        updatedPlanData
      );
      if (response.data.success) {
        toast.success(response.data.message || "Plan updated successfully!");
        fetchPlans();
        handleEditDialogClose();
      }
    } catch (error) {
      console.error("Error updating plan:", error);
      toast.error(error.response?.data?.message || "Failed to update plan");
    } finally {
      // Add a small delay to show loading state
      setTimeout(() => {
        setLoading(false);
      }, 800);
    }
  };

  // Handle toggle popular status
  const handleTogglePopular = async (plan) => {
    setLoading(true);
    try {
      const response = await api.put(`/planwithstudentreview/plan/${plan._id}`, {
        isPopular: !plan.isPopular,
      });
      if (response.data.success) {
        toast.success(
          `Plan ${!plan.isPopular ? "marked as" : "unmarked from"} popular successfully!`
        );
        fetchPlans();
      }
    } catch (error) {
      console.error("Error updating plan:", error);
      toast.error(error.response?.data?.message || "Failed to update plan");
    } finally {
      // Add a small delay to show loading state
      setTimeout(() => {
        setLoading(false);
      }, 800);
    }
  };

  // Handle toggle premium status
  const handleTogglePremium = async (plan) => {
    setLoading(true);
    try {
      const response = await api.put(`/planwithstudentreview/plan/${plan._id}`, {
        isPremium: !plan.isPremium,
      });
      if (response.data.success) {
        toast.success(
          `Plan ${!plan.isPremium ? "marked as" : "unmarked from"} premium successfully!`
        );
        fetchPlans();
      }
    } catch (error) {
      console.error("Error updating plan:", error);
      toast.error(error.response?.data?.message || "Failed to update plan");
    } finally {
      // Add a small delay to show loading state
      setTimeout(() => {
        setLoading(false);
      }, 800);
    }
  };

  // Filter plans based on search
  const filteredPlans = plans.filter(
    (plan) =>
      plan.planName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plan.subTitle?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Create table columns
  const columns = [
    { name: "sno", align: "center" },
    { name: "plan Name", align: "left" },
    { name: "sub Title", align: "left" },
    { name: "price", align: "center" },
    { name: "duration", align: "center" },
    { name: "features", align: "left" },
    { name: "isPopular", align: "center" },
    { name: "isPremium", align: "center" },
    { name: "action", align: "center" },
  ];

  // Create table rows
  const rows = filteredPlans.map((plan, index) => ({
    sno: (
      <ArgonTypography variant="caption" color="secondary" fontWeight="medium">
        {index + 1}
      </ArgonTypography>
    ),
    "plan Name": (
      <ArgonTypography variant="caption" color="secondary" fontWeight="medium">
        {plan.planName}
      </ArgonTypography>
    ),
    "sub Title": (
      <ArgonTypography variant="caption" color="secondary" fontWeight="medium">
        {plan.subTitle || "N/A"}
      </ArgonTypography>
    ),
    price: (
      <ArgonTypography variant="caption" color="secondary" fontWeight="medium">
        ₹{plan.price}
      </ArgonTypography>
    ),
    duration: (
      <ArgonTypography variant="caption" color="secondary" fontWeight="medium">
        {plan.duration}
      </ArgonTypography>
    ),
    features: (
      <ArgonBox>
        {plan.features?.slice(0, 2).map((feature, index) => (
          <ArgonTypography key={index} variant="caption" color="secondary" fontWeight="medium">
            • {feature}
          </ArgonTypography>
        ))}
        {plan.features?.length > 2 && (
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            +{plan.features.length - 2} more
          </ArgonTypography>
        )}
      </ArgonBox>
    ),
    isPopular: (
      <Switch
        checked={plan.isPopular}
        onChange={() => handleTogglePopular(plan)}
        color="primary"
        disabled={loading}
      />
    ),
    isPremium: (
      <Switch
        checked={plan.isPremium}
        onChange={() => handleTogglePremium(plan)}
        color="primary"
        disabled={loading}
      />
    ),
    action: (
      <ArgonButton variant="text" color="info" size="medium" onClick={() => handleEditClick(plan)}>
        <EditIcon fontSize="large" />
      </ArgonButton>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              p={3}
              flexWrap="wrap"
              gap={2}
            >
              <ArgonTypography variant="h6">Initialization Plan</ArgonTypography>

              <ArgonBox display="flex" gap={2} alignItems="center" flexWrap="wrap">
                <ArgonInput
                  placeholder="Search by plan name or subtitle..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ minWidth: "250px" }}
                />
              </ArgonBox>
            </ArgonBox>

            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
                "& .MuiTableRow-root": {
                  height: "60px", // Increased row height
                },
                "& .MuiTableCell-root": {
                  padding: "16px", // Increased cell padding for better spacing
                },
              }}
            >
              {loading ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    Fetching Plan ..
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Please wait while we load the latest data.
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                <ArgonBox>
                  {filteredPlans.length === 0 ? (
                    <ArgonBox p={3} textAlign="center">
                      <ArgonTypography variant="body2" color="text">
                        No plans found
                      </ArgonTypography>
                    </ArgonBox>
                  ) : (
                    <Table columns={columns} rows={rows} />
                  )}
                </ArgonBox>
              )}
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>

      {/* Edit Dialog */}
      {editDialogOpen && selectedPlan && (
        <EditPlanDialog
          open={editDialogOpen}
          onClose={handleEditDialogClose}
          plan={selectedPlan}
          onSave={handlePlanUpdate}
        />
      )}

      <Footer />
    </DashboardLayout>
  );
}

export default InitializationPlan;
